import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  Video,
  VideoUploadSession,
  VideoUploadStatus,
} from "../lib/api";

/**
 * Chunked uploader for case-scoped MP4 evidence.
 *
 * The component drives the backend's three-step protocol:
 *
 *   1. POST /uploads        — open a session, learn the chunk_size + expected_chunks.
 *   2. PUT  /uploads/.../chunks/{i} — stream one slice of the file. Per-chunk retry.
 *   3. POST /uploads/.../complete — promote the assembled file into a `videos` row.
 *
 * The chunked design lets us tolerate flaky networks (each chunk has its
 * own retry budget), survive page reloads (the session id + indices live
 * in localStorage and the server replays which chunks have already
 * landed), and stream files that wouldn't fit in a single multipart
 * request (the demo box's nginx caps individual requests at 1100m).
 *
 * Multiple files can be dropped/selected at once. They queue up and
 * upload one at a time — the per-file chunk pool already saturates the
 * link, and serialising keeps each progress bar meaningful and avoids
 * piling concurrent multi-GB session directories onto the server. More
 * files can be added while a batch is in flight.
 *
 * Concurrency: we send two chunks in parallel per upload. Higher
 * concurrency rarely helps because nginx's body_filter is the
 * bottleneck and we want to leave bandwidth for the rest of the app.
 */

// IMPORTANT: must match the mime allow-list on the server. We don't add
// anything not in the engine's _ALLOWED_VIDEO_MIME set so the eventual
// /open call won't reject us with a 415 after the user already picked
// the file. Audio-only files (.wav/.mp3) ride the same chunked-upload +
// transcription pipeline as video; they just skip the scene/keyframe/
// vision steps server-side.
const ALLOWED_MIME = new Set([
  "video/mp4",
  "video/quicktime",
  "video/x-m4v",
  "video/webm",
  "video/x-matroska",
  // Audio-only
  "audio/mpeg",   // .mp3
  "audio/mp3",    // .mp3 (some browsers)
  "audio/wav",    // .wav
  "audio/x-wav",  // .wav (some browsers)
  "audio/wave",   // .wav (some browsers)
  "audio/vnd.wave",
]);

// Browsers sometimes report an empty file.type, especially for .wav. Fall
// back to a sane MIME guessed from the extension so the allow-list check
// and the server both get a usable content type.
function guessMimeFromName(name: string): string {
  const ext = name.toLowerCase().split(".").pop() || "";
  switch (ext) {
    case "mp3":
      return "audio/mpeg";
    case "wav":
      return "audio/wav";
    case "mov":
      return "video/quicktime";
    case "m4v":
      return "video/x-m4v";
    case "webm":
      return "video/webm";
    case "mkv":
      return "video/x-matroska";
    case "mp4":
      return "video/mp4";
    default:
      return "";
  }
}

// Keep in lock-step with engine settings.VIDEO_UPLOAD_MAX_MB. We
// pre-validate here purely for UX (instant "too big" error before the
// /open round-trip) — the server is authoritative.
const HARD_CAP_MB = 8192;
const CHUNK_CONCURRENCY = 2;
const MAX_RETRIES_PER_CHUNK = 4;
// How long a finished row lingers before it clears itself out.
const DONE_LINGER_MS = 4000;

function fileIdentity(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function storageKey(caseId: number, file: File): string {
  // Resume on page reload: keyed by case + name + size + lastModified
  // so picking the *same* file rehydrates the prior session and the
  // server fills us in on which chunks already exist.
  return `cf_video_upload:${caseId}:${fileIdentity(file)}`;
}

type ItemStatus =
  | "pending"
  | "uploading"
  | "completing"
  | "complete"
  | "failed";

interface QueueItem {
  key: string;
  file: File;
  /** Captured when the file was queued, not when it starts uploading. */
  translate: boolean;
  uploadId?: string;
  uploadedBytes: number;
  status: ItemStatus;
  error?: string;
}

interface Props {
  caseId: number;
  /** Called with each freshly-created Video row as its /complete lands. */
  onUploaded: (video: Video) => void;
  /** Optional disable (e.g. case is archived). */
  disabled?: boolean;
}

export default function VideoUploader({ caseId, onUploaded, disabled }: Props) {
  const [dragging, setDragging] = useState(false);
  const [items, setItemsState] = useState<QueueItem[]>([]);
  const [translate, setTranslate] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // The queue is read by the async pump, which can't see React state
  // snapshots, so keep an always-current mirror alongside it.
  const itemsRef = useRef<QueueItem[]>([]);
  const controllersRef = useRef(new Map<string, AbortController>());
  const timersRef = useRef<number[]>([]);
  const pumpingRef = useRef(false);
  const seqRef = useRef(0);
  // Mirror the toggle into a ref so the queue handlers read the latest
  // value without churning on every toggle.
  const translateRef = useRef(false);
  translateRef.current = translate;
  const onUploadedRef = useRef(onUploaded);
  onUploadedRef.current = onUploaded;

  const commit = useCallback((next: QueueItem[]) => {
    itemsRef.current = next;
    setItemsState(next);
  }, []);

  const patchItem = useCallback(
    (key: string, patch: Partial<QueueItem>) => {
      commit(
        itemsRef.current.map((it) => (it.key === key ? { ...it, ...patch } : it)),
      );
    },
    [commit],
  );

  const dropItem = useCallback(
    (key: string) => {
      commit(itemsRef.current.filter((it) => it.key !== key));
    },
    [commit],
  );

  // If the page unmounts mid-upload, abort the in-flight chunks. The
  // session row remains on the server so the user can resume by picking
  // the same file again — the upload will skip already-uploaded chunks.
  useEffect(() => {
    const controllers = controllersRef.current;
    const timers = timersRef.current;
    return () => {
      controllers.forEach((c) => c.abort());
      controllers.clear();
      timers.forEach((t) => clearTimeout(t));
    };
  }, []);

  const runUpload = useCallback(
    async (item: QueueItem) => {
      const { key, file } = item;
      const controller = new AbortController();
      controllersRef.current.set(key, controller);
      const signal = controller.signal;
      const lsKey = storageKey(caseId, file);
      const mime = file.type || guessMimeFromName(file.name);

      patchItem(key, { status: "uploading" });

      // ----- Try to resume a prior session for this exact file -----
      let session: VideoUploadSession | null = null;
      let alreadyReceived = new Set<number>();
      let alreadyBytes = 0;
      const cachedId = localStorage.getItem(lsKey);
      if (cachedId) {
        try {
          const probed = await api.get<VideoUploadStatus>(
            `/cases/${caseId}/videos/uploads/${cachedId}`,
          );
          if (probed.status === "open") {
            session = {
              upload_id: probed.upload_id,
              chunk_size: probed.chunk_size,
              expected_chunks: probed.expected_chunks,
              received_indices: probed.received_indices,
              received_bytes: probed.received_bytes,
            };
            alreadyReceived = new Set(probed.received_indices);
            alreadyBytes = probed.received_bytes;
          } else if (probed.status === "complete" && probed.video_id) {
            // Already finalized — clear the local key and we're done.
            localStorage.removeItem(lsKey);
          }
        } catch {
          // Stale id, drop it and open a fresh session below.
          localStorage.removeItem(lsKey);
        }
      }

      try {
        if (!session) {
          session = await api.post<VideoUploadSession>(
            `/cases/${caseId}/videos/uploads`,
            {
              original_name: file.name,
              mime_type: mime,
              total_bytes: file.size,
              translate: item.translate,
            },
          );
          localStorage.setItem(lsKey, session.upload_id);
        }

        patchItem(key, {
          uploadId: session.upload_id,
          uploadedBytes: alreadyBytes,
          status: "uploading",
        });

        // ----- Slice the file + dispatch chunks with bounded concurrency -----
        const indices: number[] = [];
        for (let i = 0; i < session.expected_chunks; i++) {
          if (!alreadyReceived.has(i)) indices.push(i);
        }

        // Per-chunk progress in-flight tally so the UI bar moves smoothly
        // while a chunk is mid-flight (XHR.upload.onprogress).
        const inflight = new Map<number, number>();
        let completedBytes = alreadyBytes;

        const updateProgress = () => {
          let sum = completedBytes;
          for (const n of inflight.values()) sum += n;
          patchItem(key, { uploadedBytes: Math.min(sum, file.size) });
        };

        const sendChunk = async (idx: number) => {
          const start = idx * session!.chunk_size;
          const end = Math.min(start + session!.chunk_size, file.size);
          const blob = file.slice(start, end);
          let attempt = 0;
          for (;;) {
            try {
              inflight.set(idx, 0);
              await api.putBlob(
                `/cases/${caseId}/videos/uploads/${session!.upload_id}/chunks/${idx}`,
                blob,
                {
                  signal,
                  onProgress: (loaded) => {
                    inflight.set(idx, loaded);
                    updateProgress();
                  },
                },
              );
              inflight.delete(idx);
              completedBytes += blob.size;
              updateProgress();
              return;
            } catch (err: any) {
              inflight.delete(idx);
              if (signal.aborted) throw new Error("aborted");
              attempt += 1;
              if (attempt > MAX_RETRIES_PER_CHUNK) {
                throw err instanceof Error
                  ? err
                  : new Error("Chunk upload failed");
              }
              // Exponential backoff with jitter — capped at 8s.
              const delay =
                Math.min(8000, 500 * Math.pow(2, attempt - 1)) +
                Math.floor(Math.random() * 250);
              await new Promise((r) => setTimeout(r, delay));
            }
          }
        };

        // Manual worker pool so we can cap parallelism cheaply.
        let nextIdx = 0;
        const workers: Promise<void>[] = [];
        for (let w = 0; w < CHUNK_CONCURRENCY; w++) {
          workers.push(
            (async () => {
              while (nextIdx < indices.length) {
                const i = indices[nextIdx++];
                await sendChunk(i);
              }
            })(),
          );
        }
        await Promise.all(workers);

        patchItem(key, { status: "completing", uploadedBytes: file.size });

        const video = await api.post<Video>(
          `/cases/${caseId}/videos/uploads/${session.upload_id}/complete`,
          undefined,
        );
        localStorage.removeItem(lsKey);
        patchItem(key, { status: "complete" });
        onUploadedRef.current(video);
        // Auto-dismiss the finished row after a short beat so a long
        // batch doesn't leave a wall of green behind it.
        timersRef.current.push(
          window.setTimeout(() => dropItem(key), DONE_LINGER_MS),
        );
      } catch (err: unknown) {
        if ((err as Error)?.message === "aborted" || signal.aborted) {
          dropItem(key);
          return;
        }
        const msg =
          err instanceof ApiError
            ? err.message
            : (err as Error)?.message ?? "Upload failed";
        patchItem(key, { status: "failed", error: msg });
      } finally {
        controllersRef.current.delete(key);
      }
    },
    [caseId, patchItem, dropItem],
  );

  // Drains the queue one file at a time. Re-entrancy is guarded so that
  // adding files mid-batch just extends the run in progress.
  const pump = useCallback(async () => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    try {
      for (;;) {
        const next = itemsRef.current.find((it) => it.status === "pending");
        if (!next) break;
        // eslint-disable-next-line no-await-in-loop
        await runUpload(next);
      }
    } finally {
      pumpingRef.current = false;
    }
  }, [runUpload]);

  const enqueue = useCallback(
    (files: FileList | File[]) => {
      if (disabled) return;
      const picked = Array.from(files);
      if (!picked.length) return;

      // Two rows for the same file would share one resume key and fight
      // over the same server-side session, so skip anything already in
      // the queue and not yet finished.
      const liveIdentities = new Set(
        itemsRef.current
          .filter((it) => it.status !== "complete" && it.status !== "failed")
          .map((it) => fileIdentity(it.file)),
      );

      const added: QueueItem[] = [];
      for (const file of picked) {
        if (liveIdentities.has(fileIdentity(file))) continue;
        liveIdentities.add(fileIdentity(file));

        const key = `${fileIdentity(file)}#${seqRef.current++}`;
        const base: QueueItem = {
          key,
          file,
          translate: translateRef.current,
          uploadedBytes: 0,
          status: "pending",
        };

        const mime = file.type || guessMimeFromName(file.name);
        if (file.size === 0) {
          added.push({ ...base, status: "failed", error: "File is empty" });
        } else if (file.size > HARD_CAP_MB * 1024 * 1024) {
          added.push({
            ...base,
            status: "failed",
            error: `File exceeds the ${HARD_CAP_MB} MB cap`,
          });
        } else if (!mime || !ALLOWED_MIME.has(mime)) {
          added.push({
            ...base,
            status: "failed",
            error: `Unsupported file type: ${mime || file.name}`,
          });
        } else {
          added.push(base);
        }
      }

      if (!added.length) return;
      commit([...itemsRef.current, ...added]);
      // Tidy the input so the same file can be re-picked later.
      if (inputRef.current) inputRef.current.value = "";
      void pump();
    },
    [disabled, commit, pump],
  );

  const onDrop = useCallback(
    (ev: React.DragEvent<HTMLDivElement>) => {
      ev.preventDefault();
      setDragging(false);
      if (ev.dataTransfer.files?.length) enqueue(ev.dataTransfer.files);
    },
    [enqueue],
  );

  const onSelect = useCallback(
    (ev: React.ChangeEvent<HTMLInputElement>) => {
      if (ev.target.files?.length) enqueue(ev.target.files);
    },
    [enqueue],
  );

  const onCancel = useCallback(
    async (item: QueueItem) => {
      controllersRef.current.get(item.key)?.abort();
      controllersRef.current.delete(item.key);
      dropItem(item.key);
      // Best-effort tell the server to clean up — never block the UI on it.
      if (item.uploadId) {
        try {
          await api.del(`/cases/${caseId}/videos/uploads/${item.uploadId}`);
        } catch {
          /* ignore */
        }
      }
    },
    [caseId, dropItem],
  );

  const onCancelAll = useCallback(() => {
    for (const item of [...itemsRef.current]) {
      if (item.status === "complete" || item.status === "failed") {
        dropItem(item.key);
      } else {
        void onCancel(item);
      }
    }
  }, [dropItem, onCancel]);

  const summary = useMemo(() => {
    let done = 0;
    let failed = 0;
    let inFlight = 0;
    for (const it of items) {
      if (it.status === "complete") done += 1;
      else if (it.status === "failed") failed += 1;
      else inFlight += 1;
    }
    return { done, failed, inFlight };
  }, [items]);

  return (
    <div className="video-uploader">
      <div
        className={`dropzone ${dragging ? "dropzone--active" : ""}`}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="video/mp4,video/quicktime,video/webm,video/x-m4v,video/x-matroska,audio/mpeg,audio/mp3,audio/wav,audio/x-wav,.mp3,.wav"
          hidden
          onChange={onSelect}
        />
        <div className="dropzone__title">
          Drop video or audio files here, or click to select
        </div>
        <div className="dropzone__sub">
          Video (MP4, MOV, WebM, MKV) or audio (MP3, WAV) up to {HARD_CAP_MB >= 1024 && HARD_CAP_MB % 1024 === 0
            ? `${HARD_CAP_MB / 1024} GB`
            : `${HARD_CAP_MB} MB`} each. Pick as many as you like — they
          upload one after another. Audio files are transcribed and made
          searchable just like video. Uploads chunk automatically so a
          flaky connection can resume.
        </div>
      </div>

      {items.length > 0 && (
        <div className="video-uploader__queue">
          <div className="video-uploader__queue-head">
            <span className="muted">
              {summary.inFlight > 0
                ? `Uploading ${summary.done + 1} of ${
                    summary.done + summary.inFlight
                  }`
                : "Uploads finished"}
              {summary.failed > 0 && ` · ${summary.failed} failed`}
            </span>
            <button
              type="button"
              className="btn btn--sm"
              onClick={onCancelAll}
            >
              {summary.inFlight > 0 ? "Cancel all" : "Clear"}
            </button>
          </div>
          {items.map((item) => (
            <QueueRow
              key={item.key}
              item={item}
              onCancel={() => void onCancel(item)}
              onDismiss={() => dropItem(item.key)}
            />
          ))}
        </div>
      )}

      <label
        className="dropzone__option"
        title="Detects the spoken language and translates the transcript to English with AI during processing, so a non-English video is searchable from English questions and shows English captions. The original video is unchanged."
      >
        <input
          type="checkbox"
          checked={translate}
          disabled={disabled}
          onChange={(e) => setTranslate(e.target.checked)}
        />
        <span>
          Translate transcript to English with AI
          <span className="dropzone__option-sub">
            For Spanish, Vietnamese, or other non-English videos. English audio is left as-is.
            Applies to files added while it's checked.
          </span>
        </span>
      </label>
    </div>
  );
}

function QueueRow({
  item,
  onCancel,
  onDismiss,
}: {
  item: QueueItem;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const total = item.file.size;
  const pct =
    total > 0 ? Math.min(100, Math.round((item.uploadedBytes / total) * 100)) : 0;
  const active = item.status === "uploading" || item.status === "completing";

  return (
    <div className={`video-uploader__item video-uploader__item--${item.status}`}>
      <div className="video-uploader__item-main">
        <div className="video-uploader__item-name" title={item.file.name}>
          {item.file.name}
        </div>
        {active && (
          <div
            className="video-uploader__bar"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div
              className="video-uploader__bar-fill"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <div className="video-uploader__item-meta">
          {item.status === "pending" && `Waiting · ${formatBytes(total)}`}
          {item.status === "uploading" &&
            `${formatBytes(item.uploadedBytes)} / ${formatBytes(total)} — ${pct}%`}
          {item.status === "completing" && "Finalizing…"}
          {item.status === "complete" && "Upload complete"}
          {item.status === "failed" && (item.error ?? "Upload failed")}
        </div>
      </div>
      <div className="video-uploader__item-actions">
        {item.status === "failed" || item.status === "complete" ? (
          <button type="button" className="btn btn--sm" onClick={onDismiss}>
            Dismiss
          </button>
        ) : (
          <button type="button" className="btn btn--sm" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
